import React, { useState, useEffect, useCallback } from 'react';
import { CardSkeleton } from '../../../components/enterprise';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Key as KeyIcon,
  Computer as ComputerIcon,
  Business as BusinessIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useLicense } from '../../../contexts/LicenseContext';
import { useAuth } from '../../../contexts/AuthContext';
import licenseService from '../../../services/licenseService';
import { toServerDate } from '../../../timezone';

const formatDate = (dateString) => {
  const d = toServerDate(dateString);
  if (!d) return 'N/A';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const LicenseManagementPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin } = useAuth();
  const { licenseInfo: statusInfo, loading: statusLoading, refreshLicense, isInGracePeriod, daysRemaining, daysInGrace } = useLicense();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [machineId, setMachineId] = useState(null);
  const [loadingMachineId, setLoadingMachineId] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLicenseInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await licenseService.getLicenseInfo();
      const info = response.data?.data || response.data;
      setLicenseInfo(info);
    } catch (err) {
      console.error('Failed to fetch license info:', err);
      // Use status info as fallback
      setLicenseInfo(statusInfo);
    } finally {
      setLoading(false);
    }
  }, [statusInfo]);

  useEffect(() => {
    fetchLicenseInfo();
  }, [fetchLicenseInfo]);

  useEffect(() => {
    if (isAdmin) {
      fetchMachineId();
    }
  }, [isAdmin]);

  const fetchMachineId = async () => {
    try {
      setLoadingMachineId(true);
      const response = await licenseService.getMachineId();
      setMachineId(response.data?.data || response.data);
    } catch (err) {
      console.error('Failed to fetch machine ID:', err);
    } finally {
      setLoadingMachineId(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await refreshLicense();
      await fetchLicenseInfo();
      enqueueSnackbar('License information refreshed', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to refresh license', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopyMachineId = () => {
    if (machineId?.machineId) {
      navigator.clipboard.writeText(machineId.machineId);
      enqueueSnackbar('Machine ID copied to clipboard', { variant: 'success' });
    }
  };

  const getStatusChip = () => {
    if (!licenseInfo) return null;

    const statusConfig = {
      VALID: { label: 'Valid', color: 'success', icon: <ValidIcon /> },
      EXPIRED_IN_GRACE: { label: 'Grace Period', color: 'warning', icon: <WarningIcon /> },
      EXPIRED: { label: 'Expired', color: 'error', icon: <ErrorIcon /> },
      INVALID_SIGNATURE: { label: 'Invalid', color: 'error', icon: <ErrorIcon /> },
      INVALID_MACHINE: { label: 'Invalid Machine', color: 'error', icon: <ErrorIcon /> },
      NOT_FOUND: { label: 'Not Found', color: 'default', icon: <InfoIcon /> },
      REVOKED: { label: 'Revoked', color: 'error', icon: <ErrorIcon /> },
    };

    const config = statusConfig[licenseInfo.status] || statusConfig.NOT_FOUND;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="medium"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Box sx={{ p: 3 }}><CardSkeleton count={2} /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          License Management
        </Typography>
        {isAdmin && (
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh License
          </Button>
        )}
      </Box>

      {/* Warning Banner */}
      {isInGracePeriod && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>License Expired - Grace Period Active</AlertTitle>
          Your license has expired! You have {daysInGrace} days remaining in the grace period.
          Please renew your license to avoid service interruption.
        </Alert>
      )}

      {licenseInfo?.showWarning && !isInGracePeriod && daysRemaining !== null && daysRemaining <= 30 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>License Expiring Soon</AlertTitle>
          Your license will expire in {daysRemaining} days. Please renew soon to avoid service interruption.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* License Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <KeyIcon color="primary" />
                <Typography variant="h6">License Status</Typography>
                <Box sx={{ ml: 'auto' }}>{getStatusChip()}</Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {licenseInfo?.statusMessage || 'License information not available'}
              </Typography>

              {licenseInfo?.licenseKey && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    License Key
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {licenseInfo.licenseKey}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Validity Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarIcon color="primary" />
                <Typography variant="h6">Validity Period</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Issue Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(licenseInfo?.issueDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Expiration Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(licenseInfo?.expirationDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Grace Period End
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(licenseInfo?.graceExpirationDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Days Remaining
                  </Typography>
                  <Typography
                    variant="h5"
                    color={
                      daysRemaining !== null && daysRemaining > 30 ? 'success.main' :
                      daysRemaining !== null && daysRemaining > 0 ? 'warning.main' : 'error.main'
                    }
                  >
                    {daysRemaining !== null && daysRemaining > 0
                      ? daysRemaining
                      : isInGracePeriod
                        ? `Grace: ${daysInGrace}`
                        : 'Expired'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Info Card */}
        {licenseInfo?.customerName && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6">Customer Information</Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Customer Name
                    </Typography>
                    <Typography variant="body1">{licenseInfo.customerName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">{licenseInfo.customerLocation || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{licenseInfo.customerEmail || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Machine Info Card (Admin Only) */}
        {isAdmin && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ComputerIcon color="primary" />
                  <Typography variant="h6">Machine Information</Typography>
                </Box>

                {loadingMachineId ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : machineId ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Machine ID (Copy this for license generation)
                    </Typography>
                    <TextField
                      fullWidth
                      value={machineId.machineId || ''}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy to clipboard">
                              <IconButton onClick={handleCopyMachineId} edge="end">
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          CPU Cores
                        </Typography>
                        <Typography variant="body1">{machineId.cpuCores}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Hostname
                        </Typography>
                        <Typography variant="body1">{machineId.hostname}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          OS
                        </Typography>
                        <Typography variant="body1">{machineId.osName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Architecture
                        </Typography>
                        <Typography variant="body1">{machineId.osArch}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Unable to retrieve machine information
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Help Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Need Help?
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          If you need to renew your license or have questions about licensing, please contact our support team.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="body2">
            <strong>Email:</strong> support@verops.io
          </Typography>
        </Box>
        {isAdmin && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            When contacting support, please include your Machine ID shown above.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default LicenseManagementPage;
